/**
 * Regeln wie im Angebotsformular (OfferFormView): Belegung + Saison-Anreise/Abreise/Mindestaufenthalt.
 */
import { addDaysYmd, enumerateHalfOpen, formatYmdFromDate } from './calendarDate'

export type WidgetAvailabilityBlock = {
  kind: string
  entity_id: string
  stay_from: string
  stay_to: string
  status_code: string
  status_label: string
  color_token: string
}

export type WidgetSeasonPeriod = {
  season_name: string
  start_month: number
  start_day: number
  end_month: number
  end_day: number
  wraps_new_year: boolean
  min_stay_nights: number
  allowed_arrival_weekdays: number[]
  allowed_departure_weekdays: number[]
}

export type WidgetStayConstraints = {
  availability_blocks: WidgetAvailabilityBlock[]
  season_periods: WidgetSeasonPeriod[]
}

/** Halb-offen [from, to) — gleiche Semantik wie Backend. */
export function enumerateDates(from: string, to: string): string[] {
  return enumerateHalfOpen(from, to)
}

/** Lokaler Kalendertag aus `Date` (kein UTC-Slice). */
export function toIsoLocal(date: Date): string {
  return formatYmdFromDate(date)
}

export const addDaysIso = addDaysYmd

function dayOfYear(date: Date): number {
  const start = new Date(date.getFullYear(), 0, 0)
  return Math.floor((date.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
}

export function dateInPeriod(date: Date, p: WidgetSeasonPeriod): boolean {
  const d = dayOfYear(date)
  const start = dayOfYear(new Date(date.getFullYear(), p.start_month - 1, p.start_day))
  const end = dayOfYear(new Date(date.getFullYear(), p.end_month - 1, p.end_day))
  if (p.wraps_new_year) return d >= start || d <= end
  return d >= start && d <= end
}

export function weekdayNumberFromIsoDate(value: string): number | null {
  const d = new Date(`${value.trim()}T00:00:00`)
  if (Number.isNaN(d.getTime())) return null
  return ((d.getDay() + 6) % 7) + 1
}

export function periodForDate(iso: string, periods: WidgetSeasonPeriod[]): WidgetSeasonPeriod | null {
  if (periods.length === 0) return null
  const d = new Date(`${iso.trim()}T00:00:00`)
  if (Number.isNaN(d.getTime())) return null
  return periods.find((p) => dateInPeriod(d, p)) ?? null
}

export function buildBlockedSet(blocks: WidgetAvailabilityBlock[]): Set<string> {
  const s = new Set<string>()
  for (const b of blocks) {
    for (const d of enumerateDates(b.stay_from, b.stay_to)) s.add(d)
  }
  return s
}

/** Anreise- bzw. Abreisetag je Block (Kalender-Halbtags-Schraffur). */
export function blockStayFromSet(blocks: WidgetAvailabilityBlock[]): Set<string> {
  const s = new Set<string>()
  for (const b of blocks) {
    const d = String(b.stay_from ?? '').trim()
    if (d) s.add(d)
  }
  return s
}

export function blockStayToSet(blocks: WidgetAvailabilityBlock[]): Set<string> {
  const s = new Set<string>()
  for (const b of blocks) {
    const d = String(b.stay_to ?? '').trim()
    if (d) s.add(d)
  }
  return s
}

export type CalendarConstraintCtx = {
  blocked: Set<string>
  periods: WidgetSeasonPeriod[]
  blocks: WidgetAvailabilityBlock[]
  stayFromDays: Set<string>
  stayToDays: Set<string>
  todayIso: string
}

export function buildConstraintCtx(constraints: WidgetStayConstraints, todayIso: string): CalendarConstraintCtx {
  const blocks = constraints.availability_blocks
  return {
    blocked: buildBlockedSet(blocks),
    periods: constraints.season_periods,
    blocks,
    stayFromDays: blockStayFromSet(blocks),
    stayToDays: blockStayToSet(blocks),
    todayIso,
  }
}

/** Nacht-Intervalle halb-offen [from, to) wie im Backend — Ueberlappung von Aufenthalten. */
export function stayHalfOpenOverlaps(stayFrom: string, stayTo: string, blockFrom: string, blockTo: string): boolean {
  const a = stayFrom.trim()
  const c = stayTo.trim()
  const b = blockFrom.trim()
  const d = blockTo.trim()
  if (!a || !c || !b || !d) return false
  return a < d && b < c
}

export function overlapsAnyBlock(stayFrom: string, stayTo: string, blocks: WidgetAvailabilityBlock[]): boolean {
  for (const b of blocks) {
    if (stayHalfOpenOverlaps(stayFrom, stayTo, b.stay_from, b.stay_to)) return true
  }
  return false
}

export function isStartDisabled(iso: string, ctx: CalendarConstraintCtx | null): boolean {
  if (!ctx) return false
  if (iso < ctx.todayIso) return true
  if (ctx.blocked.has(iso)) return true
  if (ctx.periods.length === 0) return false
  const period = periodForDate(iso, ctx.periods)
  if (!period) return true
  const w = weekdayNumberFromIsoDate(iso)
  if (w == null || !period.allowed_arrival_weekdays.includes(w)) return true
  return false
}

export function startDisabledReason(iso: string, ctx: CalendarConstraintCtx | null): string {
  if (!ctx) return ''
  if (iso < ctx.todayIso) return 'Vergangene Tage sind nicht waehlbar.'
  if (ctx.blocked.has(iso)) {
    for (const b of ctx.blocks) {
      for (const d of enumerateDates(b.stay_from, b.stay_to)) {
        if (d === iso) return `Tag ist durch „${b.status_label}“ blockiert.`
      }
    }
    return 'Tag ist blockiert.'
  }
  if (ctx.periods.length === 0) return ''
  const period = periodForDate(iso, ctx.periods)
  if (!period) return 'Tag liegt ausserhalb eines gueltigen Saison-Zeitraums.'
  const w = weekdayNumberFromIsoDate(iso)
  if (w == null || !period.allowed_arrival_weekdays.includes(w)) {
    return `Anreise an diesem Wochentag nicht erlaubt (Saison: ${period.season_name}).`
  }
  return ''
}

export function rangeEndMinIso(startIso: string, period: WidgetSeasonPeriod | null): string {
  const minNights = Math.max(1, period?.min_stay_nights ?? 1)
  return addDaysIso(startIso, minNights) ?? ''
}

export function isEndDisabled(iso: string, startIso: string, ctx: CalendarConstraintCtx | null): boolean {
  if (!ctx) return false
  if (iso < ctx.todayIso) return true
  if (!startIso) return false
  if (iso <= startIso) return true
  /* Abreisetag `iso` ist exklusives Ende [start, iso) — nicht mit blocked.has(iso)
   * pruefen: Folgebelegung ab `iso` liegt im selben Kalendertag, ohne Nacht-Ueberlappung. */
  if (ctx.blocks.length > 0 && overlapsAnyBlock(startIso, iso, ctx.blocks)) return true
  const rule = periodForDate(startIso, ctx.periods)
  const minEnd = rangeEndMinIso(startIso, rule)
  if (minEnd && iso < minEnd) return true
  if (rule) {
    const w = weekdayNumberFromIsoDate(iso)
    if (w == null || !rule.allowed_departure_weekdays.includes(w)) return true
  }
  return false
}

export function endDisabledReason(iso: string, startIso: string, ctx: CalendarConstraintCtx | null): string {
  if (!ctx) return ''
  if (iso < ctx.todayIso) return 'Vergangene Tage sind nicht waehlbar.'
  if (!startIso) return ''
  if (iso <= startIso) return 'Abreise muss nach Anreise liegen.'
  if (ctx.blocks.length > 0 && overlapsAnyBlock(startIso, iso, ctx.blocks)) {
    for (const b of ctx.blocks) {
      if (stayHalfOpenOverlaps(startIso, iso, b.stay_from, b.stay_to)) {
        return `Zeitraum ueberlappt mit „${b.status_label}“ (${b.stay_from} – ${b.stay_to}).`
      }
    }
    return 'Zeitraum ueberlappt mit bestehender Belegung.'
  }
  const rule = periodForDate(startIso, ctx.periods)
  const minEnd = rangeEndMinIso(startIso, rule)
  if (minEnd && iso < minEnd) {
    const n = Math.max(1, rule?.min_stay_nights ?? 1)
    return n <= 1 ? 'Abreise muss nach der Anreise liegen.' : `Mindestaufenthalt: ${n} Naechte.`
  }
  if (rule) {
    const w = weekdayNumberFromIsoDate(iso)
    if (w == null || !rule.allowed_departure_weekdays.includes(w)) {
      return `Abreise an diesem Wochentag nicht erlaubt (Saison: ${rule.season_name}).`
    }
  }
  return ''
}

